import { create } from 'zustand';
import { isAxiosError } from 'axios';
import { useCases } from '../../infrastructure/di/container';
import { getErrorMessage } from '../../infrastructure/http/common';
import { CrawlerChannelEntity } from '../../domain/entities/CrawlerChannel';
import { useCrawlersStore } from './crawlers.store';

const toErrorSlug = (e: unknown): string => {
  if (isAxiosError(e)) return getErrorMessage(e);
  if (e instanceof Error && e.message) return e.message;
  return 'unknown-error';
};

interface CrawlerChannelsState {
  crawlerUUID?: string;
  items: CrawlerChannelEntity[];
  isLoading: boolean;
  isSaving: boolean;
  error?: string;
  load: (crawlerUUID: string) => Promise<void>;
  assign: (crawlerUUID: string, channelUuids: string[]) => Promise<void>;
  unassign: (crawlerUUID: string, id: number) => Promise<void>;
  reset: () => void;
}

export const useCrawlerChannelsStore = create<CrawlerChannelsState>((set, get) => {
  const mutate = async (crawlerUUID: string, action: () => Promise<void>): Promise<void> => {
    set({ isSaving: true });
    try {
      await action();
      await Promise.all([get().load(crawlerUUID), useCrawlersStore.getState().load()]);
    } catch (e) {
      throw new Error(toErrorSlug(e));
    } finally {
      set({ isSaving: false });
    }
  };

  return {
    items: [],
    isLoading: false,
    isSaving: false,
    async load(crawlerUUID) {
      if (get().crawlerUUID !== crawlerUUID) set({ items: [] });
      set({ crawlerUUID, isLoading: true, error: undefined });
      try {
        const items = await useCases.listCrawlerChannels().execute(crawlerUUID);
        if (get().crawlerUUID === crawlerUUID) set({ items, isLoading: false });
      } catch (e) {
        set({ isLoading: false, error: toErrorSlug(e) });
      }
    },
    assign(crawlerUUID, channelUuids) {
      return mutate(crawlerUUID, () => useCases.assignCrawlerChannels().execute(crawlerUUID, channelUuids));
    },
    unassign(crawlerUUID, id) {
      return mutate(crawlerUUID, () => useCases.unassignCrawlerChannel().execute(crawlerUUID, id));
    },
    reset() {
      set({ crawlerUUID: undefined, items: [], isLoading: false, error: undefined });
    },
  };
});
